import { useMemo } from 'react'
import { KpiTile } from '../../charts/KpiTile'
import { WidgetFrame } from '../../widgets/WidgetFrame'
import { useWidgetData } from '../../widgets/useWidgetData'
import { WIDGET_KEY, type WidgetProps } from '../../widgets/types'
import { buildDrillRsql } from '../../drill'
import { formatMoney } from '../../format'
import { sumTotalCertifiedValue } from './analyticsMappers'
import type { CreditValueByJurisdictionResult } from '../../../../types/insights'

const WIDGET_KEY_SELF = WIDGET_KEY.creditValueByJurisdictionFy

/** Same disclosed constant as every other certified-value tile — every certified-value fact at launch is USD. */
const REPORTING_CURRENCY = 'USD'

/**
 * AC1.1 KPI — "Total certified value": the corrected I-47 form. The parent DoR's original
 * "$ SUM per lifecycle stage" is not buildable (`ProgramFilingState` is `DRAFT`/`CERTIFIED` only —
 * see `docs/raid-log.md` I-47); this tile gives a real, honest $ figure instead — the SUM of
 * certified value across all visible filings, reusing the SAME `credit-value-by-jurisdiction-fy`
 * payload `TopJurisdictionsWidget` (AC1.4) already fetches. No new backend read.
 */
export function TotalCertifiedValueKpiWidget({ filter, skipRefetch, onDrill }: WidgetProps) {
  const { status, data, message, retry } =
    useWidgetData<CreditValueByJurisdictionResult>(WIDGET_KEY_SELF, filter, skipRefetch)

  const buckets = useMemo(() => (Array.isArray(data?.buckets) ? data.buckets : []), [data])
  const total = useMemo(() => sumTotalCertifiedValue(buckets), [buckets])
  const excludedCount = typeof data?.excludedCount === 'number' && data.excludedCount > 0 ? data.excludedCount : 0

  // AC1.1 failure path — "—" (never "$0") on error/denial, distinct from a genuine $0 portfolio.
  const frameState = status === 'loading' ? 'loading' : status === 'not_authorized' ? 'not_authorized' : status === 'error' ? 'error' : 'ok'

  return (
    <WidgetFrame
      title="Total certified value"
      icon="dollar-sign"
      state={frameState}
      message={message}
      onRetry={retry}
      drill={{ label: 'View filings', onClick: () => onDrill(buildDrillRsql(filter)) }}
    >
      <KpiTile
        title="Total certified value"
        value={status === 'ok' ? formatMoney(String(total), REPORTING_CURRENCY) : '—'}
        valueCaption={`across ${buckets.filter((b) => Number(b.sumCertifiedAmount) > 0).length} jurisdiction(s)`}
        breakdown={
          excludedCount > 0
            ? [{ key: 'excluded', label: 'Excluded (non-USD)', value: String(excludedCount) }]
            : []
        }
      />
    </WidgetFrame>
  )
}
